"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import ChatContextHeader from "@/components/chat/ChatContextHeader";
import ChatBubble from "@/components/ChatBubble";
import ChatInput from "@/components/ChatInput";
import AgentTyping from "@/components/AgentTyping";
import RevealBanner from "@/components/chat/RevealBanner";
import { useAssistantContext } from "@/hooks/useAssistantContext";
import { useOverlayManager } from "@/components/overlays/OverlayManager";
import { useChatSessionStore } from "@/lib/stores/chatSession";
import { useChatRevealStore } from "@/lib/stores/chatReveal";
import { sendChat, checkHealth, executeAction, generateSchedule } from "@/lib/api";
import {
  detectAndNotifyActions,
  notifyFromActionResult,
  notifyFromActionReveal,
} from "@/lib/store";
import { useAuth } from "@/lib/store";
import type { ActionReveal, AppProposal, ChatApiResponse, ChatMessage, NextAction } from "@/lib/api";
import { formatLocalDateInput } from "@/lib/utils";
import { InlineFanoutPanel } from "@/components/chat/InlineFanoutPanel";
import { surfaceLabel } from "@/lib/stores/chatReveal";

let msgCounter = 0;
function newId() {
  return `msg-${Date.now()}-${++msgCounter}`;
}

interface Props {
  /** Which screen the user is viewing — used for context-aware behavior */
  screenContext?: string;
  /** If set, this message is sent automatically when the screen mounts */
  initialMessage?: string | null;
  onInitialMessageConsumed?: () => void;
  /** WIP-1 § 8-7 U5: id of the goal the user has in focus, forwarded to
   *  `sendChat()` which only attaches it to the request when
   *  screenContext === "goals". `null` on every other surface. */
  focusedGoalId?: string | null;
}

export default function ChatScreen({
  screenContext,
  initialMessage,
  onInitialMessageConsumed,
  focusedGoalId = null,
}: Props) {
  const [backendOk, setBackendOk] = useState<boolean | null>(null);
  // captureALL Phase 2: store the last fan-out response so the placeholder
  // (and later InlineFanoutPanel / CaptureAllPopup) can render proposals.
  const [fanoutResponse, setFanoutResponse] = useState<ChatApiResponse | null>(null);
  // captureALL Phase 3: success toast state set by InlineFanoutPanel onAllSaved.
  const [fanoutToast, setFanoutToast] = useState<{
    total: number;
    reveals: ActionReveal[];
  } | null>(null);
  const messages = useChatSessionStore((s) => s.messages);
  const isLoading = useChatSessionStore((s) => s.isLoading);
  const lastAgentName = useChatSessionStore((s) => s.lastAgentName);
  const lastAgentColor = useChatSessionStore((s) => s.lastAgentColor);
  const appendMessage = useChatSessionStore((s) => s.appendMessage);
  const setLoading = useChatSessionStore((s) => s.setLoading);
  const setLastAgent = useChatSessionStore((s) => s.setLastAgent);
  const bindUser = useChatSessionStore((s) => s.bindUser);

  // codex follow-up 2026-05-25 — bind the chat-session store to the
  // current auth user. This is what makes same-tab reload restore
  // the transcript for the same user, AND wipes it when a different
  // user signs in. The store reads this on every render, so logout
  // (user → null) and re-login as someone else both fire.
  const authUserId = useAuth((s) => s.user?.id ?? null);
  useEffect(() => {
    bindUser(authUserId);
  }, [authUserId, bindUser]);
  const { context: assistantContext, status: assistantContextStatus } = useAssistantContext();
  const overlays = useOverlayManager();
  const requestReveal = useChatRevealStore((s) => s.request);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const initialSentRef = useRef(false);

  const handleRevealOpen = useCallback(
    (reveal: ActionReveal) => {
      requestReveal(reveal);
      overlays.closeChat();
    },
    [overlays, requestReveal]
  );

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    checkHealth()
      .then(() => setBackendOk(true))
      .catch(() => setBackendOk(false));
  }, []);

  const handleSend = useCallback(
    async (text: string) => {
      const userMsg: ChatMessage = {
        id: newId(),
        role: "user",
        content: text,
        timestamp: Date.now(),
      };
      appendMessage(userMsg);
      setLoading(true);

      // REST fallback
      try {
        const result = await sendChat(text, screenContext, focusedGoalId);
        setLastAgent({ name: result.agent_name, color: result.agent_color });

        // captureALL Phase 2: detect fan-out responses and store for
        // placeholder render (InlineFanoutPanel/CaptureAllPopup in Phase 3/4).
        if (result.proposals && result.proposals.length > 0) {
          setFanoutResponse(result);
        } else {
          setFanoutResponse(null);
        }

        if (result.data) {
          detectAndNotifyActions(result.data as Record<string, unknown>);
        }
        // Strict UAT 2026-05-24 contract: refresh downstream surfaces
        // named in action_result.refresh (canonical) regardless of
        // legacy data-shape heuristics above.
        if (result.action_result) {
          notifyFromActionResult(result.action_result);
        }

        // Lift the canonical action_result onto `data.undo_action` so
        // ChatBubble's existing renderer picks up the Undo chip via
        // its `readUndoAction(data)` reader. Frontends that later
        // switch to reading `action_result.undo_action` directly will
        // see the same payload.
        const liftedData: Record<string, unknown> = {
          ...((result.data as Record<string, unknown>) || {}),
        };
        if (result.action_result?.undo_action && !liftedData.undo_action) {
          liftedData.undo_action = result.action_result.undo_action;
        }
        if (result.action_result && !liftedData.app_action_result) {
          liftedData.app_action_result = result.action_result;
        }

        const assistantMsg: ChatMessage = {
          id: newId(),
          role: "assistant",
          content: result.response_text,
          response_type: result.response_type,
          intent: result.intent,
          data: liftedData,
          agent_name: result.agent_name,
          agent_color: result.agent_color,
          memory_refs: result.memory_refs,
          suggestions: result.suggestions,
          next_actions: result.next_actions,
          response_mode: result.response_mode,
          schedule_blocks: result.schedule_blocks,
          // Phase 11-5: forward the visible learning card so ChatBubble
          // can render it inline with the "That's not me" affordance.
          learning_card: result.learning_card ?? null,
          timestamp: Date.now(),
        };
        appendMessage(assistantMsg);
      } catch (err) {
        const errorMsg: ChatMessage = {
          id: newId(),
          role: "assistant",
          content:
            err instanceof Error
              ? `Hmm, something went wrong: ${err.message}. Try again?`
              : "Something went wrong. Try again?",
          response_type: "error",
          timestamp: Date.now(),
        };
        appendMessage(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [appendMessage, focusedGoalId, screenContext, setLastAgent, setLoading]
  );

  // Handle initial message from quick actions
  useEffect(() => {
    if (initialMessage && !initialSentRef.current) {
      initialSentRef.current = true;
      handleSend(initialMessage);
      onInitialMessageConsumed?.();
    }
  }, [initialMessage, handleSend, onInitialMessageConsumed]);

  return (
    <div className="flex flex-col h-full">
      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-2xl mx-auto">
          {backendOk !== false && (
            <ChatContextHeader
              context={assistantContext}
              status={assistantContextStatus}
              screenContext={screenContext}
            />
          )}

          {/* Welcome state */}
          {messages.length === 0 && (
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 rounded-lg bg-forest flex items-center justify-center">
                  <span className="text-white text-xs font-bold">DE</span>
                </div>
              </div>
              <h2 className="text-lg font-semibold text-text">
                What&apos;s on your mind?
              </h2>

              {/* Connection status */}
              {backendOk === false && (
                <div className="mt-6 p-3 bg-amber/10 border border-amber/30 rounded-xl text-sm text-amber max-w-sm mx-auto">
                  Backend not reachable. Make sure{" "}
                  <code className="bg-amber/15 px-1 rounded">uvicorn</code>{" "}
                  is running on :8000.
                </div>
              )}
            </div>
          )}

          {/* Message list. Action-result bubbles with a reveal target
              render the U24 banner above the bubble so the user has a
              visible "land me on this" affordance instead of a generic
              "Done!" toast. */}
          {messages.map((msg) => (
            <div key={msg.id}>
              {msg.reveal ? (
                <RevealBanner reveal={msg.reveal} onOpen={handleRevealOpen} />
              ) : null}
              <ChatBubble
                message={msg}
                onSuggestionSelect={handleSend}
                onActionClick={async (actionId, actionType) => {
                setLoading(true);
                try {
                  // Handle "Confirm & Add to Plan" from planning flow
                  if (actionType === "confirm_plan") {
                    const today = formatLocalDateInput(new Date());
                    const generated = await generateSchedule({ date: today });
                    const firstBlock = generated.blocks?.[0] ?? null;
                    const reveal: ActionReveal = {
                      surface: "plan",
                      id: generated.focus_block_id || firstBlock?.id || "today",
                      label: firstBlock?.title || "Plan",
                    };
                    detectAndNotifyActions({
                      intent: "daily_planning",
                      schedule_generated: generated.total > 0,
                    });
                    notifyFromActionReveal(reveal);
                    // ISSUE-004: notifyFromActionReveal only REFRESHES the
                    // destination; navigation lives in the chat-reveal store.
                    // Drive it on the single chip click (mirrors the working
                    // RevealBanner path) so the user actually lands on Plan.
                    requestReveal(reveal);
                    overlays.closeChat();
                    const confirmMsg: ChatMessage = {
                      id: newId(),
                      role: "assistant",
                      content:
                        generated.total > 0
                          ? `Saved ${generated.total} block${
                              generated.total === 1 ? "" : "s"
                            } to Plan.`
                          : "I couldn't find anything to schedule yet. Open Plan to add a block.",
                      response_type: "action_result",
                      agent_name: lastAgentName,
                      agent_color: lastAgentColor,
                      reveal,
                      suggestions: ["Show me my schedule", "What else should I do today?"],
                      timestamp: Date.now(),
                    };
                    appendMessage(confirmMsg);
                    setLoading(false);
                    return;
                  }
                  const res = await executeAction(actionId);
                  const reveal = res.reveal ?? null;
                  if (res.result) {
                    detectAndNotifyActions(res.result);
                  }
                  if (reveal) {
                    notifyFromActionReveal(reveal);
                    // ISSUE-004: also NAVIGATE (not just refresh) on the chip
                    // click, via the chat-reveal store page.tsx subscribes to.
                    requestReveal(reveal);
                    overlays.closeChat();
                  }
                  // Phase 9-1 UAT fix (2026-05-24): the executor now
                  // returns `undo_action` in `res.result` for goal /
                  // task creations so confirm-first paths get the same
                  // Undo affordance as the autonomous path. Lift it to
                  // the bubble's `data` so ChatBubble's existing undo
                  // chip renderer picks it up.
                  const resultUndo =
                    typeof res.result === "object" &&
                    res.result !== null &&
                    "undo_action" in res.result
                      ? (res.result as Record<string, unknown>).undo_action
                      : undefined;
                  if (res.follow_up) {
                    const followUpMsg: ChatMessage = {
                      id: newId(),
                      role: "assistant",
                      content: res.follow_up.message,
                      response_type: resultUndo ? "action" : "action_result",
                      intent: res.follow_up.intent,
                      next_actions: res.follow_up.next_actions || [],
                      response_mode: res.follow_up.response_mode,
                      agent_name: lastAgentName,
                      agent_color: lastAgentColor,
                      reveal,
                      data: resultUndo ? { undo_action: resultUndo } : undefined,
                      timestamp: Date.now(),
                    };
                    appendMessage(followUpMsg);
                  } else if (res.status === "dismissed") {
                    // No message needed for dismiss
                  } else if (res.status === "success" || res.status === "already_completed") {
                    // U24 — When the backend returns a reveal target,
                    // surface it inline so the user can land on the
                    // thing that just got created. Falls back to a
                    // plain "Done!" when no reveal (dismiss / plan_day).
                    const doneMsg: ChatMessage = {
                      id: newId(),
                      role: "assistant",
                      content: reveal ? "" : "Done!",
                      response_type: "action_result",
                      agent_name: lastAgentName,
                      agent_color: lastAgentColor,
                      reveal,
                      timestamp: Date.now(),
                    };
                    appendMessage(doneMsg);
                  }
                } catch (err) {
                  // Dogfood R3 bug #2 (silent-failure half): previously
                  // this caught with no parameter and rendered a generic
                  // "That action didn't work. Try again?" — user had no
                  // idea what failed. Surface the actual error message
                  // when one is available so the user can self-diagnose
                  // (network down, backend 500, validation message, etc.)
                  // and the developer console gets the full payload.
                  const detail =
                    err instanceof Error && err.message
                      ? err.message
                      : null;
                  // Telemetry: always log structured detail to console
                  // regardless of whether we surface it in the bubble.
                  // eslint-disable-next-line no-console
                  console.error("[chat] executeAction failed", { actionId, actionType, err });
                  const errMsg: ChatMessage = {
                    id: newId(),
                    role: "assistant",
                    content: detail
                      ? `That didn't go through — ${detail}. Try again?`
                      : "That didn't go through. Check your connection and try again, or open the destination tab to confirm whether the action landed.",
                    response_type: "error",
                    timestamp: Date.now(),
                  };
                  appendMessage(errMsg);
                } finally {
                  setLoading(false);
                }
              }}
              />
            </div>
          ))}

          {/* Loading indicator — agent-aware */}
          {isLoading && (
            <AgentTyping agentName={lastAgentName} agentColor={lastAgentColor} />
          )}

          <div ref={chatEndRef} />
        </div>
      </div>

      {/* captureALL fan-out review. ADR-0006: ≤4 items render inline, ≥5 in a
          modal. Same InlineFanoutPanel + commit/edit/dismiss logic; only the
          container variant differs (Phase 3 inline + Phase 4 modal). */}
      {fanoutResponse &&
        (fanoutResponse.proposals?.length ?? 0) > 0 &&
        (fanoutResponse.proposal_mode === "inline" ||
          fanoutResponse.proposal_mode === "modal") && (
          <InlineFanoutPanel
            proposals={fanoutResponse.proposals ?? []}
            variant={
              fanoutResponse.proposal_mode === "modal" ? "modal" : "inline"
            }
            onAllSaved={(summary) => {
              setFanoutToast(summary);
              // Inline panel persists (saved checkmarks stay visible; collapsing
              // it on the final commit would be a harsh jump — calm-transition
              // rule). A full-screen MODAL, by contrast, should not linger over
              // the app once everything is saved — close it and let the
              // chat-level toast carry the deep-links.
              if (fanoutResponse.proposal_mode === "modal") {
                setFanoutResponse(null);
              }
            }}
            onDismissPanel={() => {
              setFanoutResponse(null);
              setFanoutToast(null);
            }}
          />
        )}

      {/* captureALL Phase 3 — success toast (rendered by ChatScreen, not panel) */}
      {fanoutToast && (
        <FanoutSuccessToast
          total={fanoutToast.total}
          reveals={fanoutToast.reveals}
          onRevealClick={handleRevealOpen}
          onDismiss={() => {
            // The fan-out is fully resolved once the toast is dismissed —
            // clear the (now all-saved) panel too so the chat returns to a
            // clean state instead of leaving a stale committed summary.
            setFanoutToast(null);
            setFanoutResponse(null);
          }}
        />
      )}

      {/* Chat input (pinned to bottom, above nav) */}
      <div className="pb-16">
        <ChatInput onSend={handleSend} isLoading={isLoading} />
      </div>
    </div>
  );
}

// ── FanoutSuccessToast ────────────────────────────────────────────────────────

/**
 * captureALL Phase 3 — shown by ChatScreen once all InlineFanoutPanel rows
 * reach 'saved'. Groups reveals by surface and renders one deep-link chip
 * per distinct surface.
 */
interface FanoutSuccessToastProps {
  total: number;
  reveals: ActionReveal[];
  onRevealClick: (reveal: ActionReveal) => void;
  onDismiss: () => void;
}

function FanoutSuccessToast({
  total,
  reveals,
  onRevealClick,
  onDismiss,
}: FanoutSuccessToastProps) {
  // Group reveals by surface — keep one representative ActionReveal per surface.
  const surfaceMap = new Map<string, ActionReveal>();
  for (const reveal of reveals) {
    if (!surfaceMap.has(reveal.surface)) {
      surfaceMap.set(reveal.surface, reveal);
    }
  }

  // Build summary label e.g. "Today (1) · Plan (1) · Library (1)"
  const surfaceCounts = new Map<string, number>();
  for (const reveal of reveals) {
    surfaceCounts.set(reveal.surface, (surfaceCounts.get(reveal.surface) ?? 0) + 1);
  }
  const summaryParts = Array.from(surfaceCounts.entries()).map(
    ([surface, count]) => `${surfaceLabel(surface)} (${count})`
  );
  const summaryText =
    summaryParts.length > 0 ? summaryParts.join(" · ") : "";

  return (
    <div
      data-testid="fanout-success-toast"
      role="status"
      aria-live="polite"
      className="mx-4 mb-2 flex flex-col gap-2 rounded-xl border border-accent/20
                 bg-accent/[0.06] px-4 py-3 shadow-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full
                       bg-accent text-white"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            >
              <path
                d="M5 12l5 5L20 7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div>
            <p className="text-sm font-semibold text-text">
              Saved {total}
            </p>
            {summaryText && (
              <p className="text-[11px] text-muted">{summaryText}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="rounded-full p-1 text-muted hover:text-text transition-colors
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med/40"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <path
              d="M18 6L6 18M6 6l12 12"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Deep-link chips — one per distinct surface */}
      {surfaceMap.size > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {Array.from(surfaceMap.entries()).map(([surface, reveal]) => (
            <button
              key={surface}
              type="button"
              data-testid={`fanout-deeplink-${surface}`}
              onClick={() => onRevealClick(reveal)}
              className="inline-flex items-center gap-1 rounded-full bg-forest-med
                         px-3 py-1 text-[11px] font-medium text-white
                         transition-transform hover:bg-forest hover:scale-[1.02]
                         active:scale-[0.98] motion-reduce:transition-none
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-forest-med/40 focus-visible:ring-offset-1"
              aria-label={`Open ${surfaceLabel(surface)}`}
            >
              {surfaceLabel(surface)}
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                aria-hidden="true"
              >
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
